import React, { useMemo, useState } from 'react';
import { useInventoryPlanning } from '../../contexts/InventoryPlanningContext';
import MonthlyPivotTable from '../MonthlyPivotTable';
import SelectSupplierLocationModal from '../SelectSupplierLocationModal';
import ArticleIdCell from '../ArticleIdCell';
import { Download } from 'lucide-react';
import { exportPivotedTableToCSV } from '../../utils/csvExport';
import { formatIntegerWithColor } from '../../utils/numberFormatting';

const ProjectedStockTab: React.FC = () => {
  const { data, effectiveProjectedInventory, selectedArticleIds, ensureSections, sectionStates } = useInventoryPlanning();
  React.useEffect(() => {
    ensureSections(['projection_inputs']);
  }, [ensureSections]);
  const projectionLoading = sectionStates.projection_inputs?.loading;
  const projectionError = sectionStates.projection_inputs?.error;
  const allRows = useMemo(() => effectiveProjectedInventory || [], [effectiveProjectedInventory]);
  
  // Normalize all rows for table display
  const normalized = useMemo(() => allRows.map((r: any) => ({
    sku_id: r.sku_id,
    month_idx: r.month,
    value: (r.has_onhand === true) ? r.projected_onhand : null,
  })), [allRows]);
  
  const [modalArticle, setModalArticle] = useState<string | null>(null);

  const handleExportStock = () => {
    // Filter by selected articles for export
    const rowsToExport = (() => {
      if (!selectedArticleIds || selectedArticleIds.length === 0) {
        return normalized; // Export all if no selection
      }
      return normalized.filter((row: any) => 
        selectedArticleIds.includes(String(row.sku_id))
      );
    })();
    
    const baseMonth = (data as any)?.as_of_date?.slice(0, 7);
    const csvContent = exportPivotedTableToCSV(
      rowsToExport,
      'sku_id',
      'month_idx',
      'value',
      baseMonth
    );
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `stock_projete_${new Date().toISOString().slice(0, 10)}.csv`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white shadow rounded p-4" style={{ minWidth: '600px' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-medium">Stock projeté</div>
        <button
          onClick={handleExportStock}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
          title="Exporter le stock projeté"
        >
          <Download size={14} />
          Exporter
        </button>
      </div>
      {projectionLoading && <div className="text-xs text-gray-500 mb-2">Chargement des données de projection…</div>}
      {projectionError && <div className="text-xs text-red-600 mb-2">{projectionError}</div>}
      <div className="text-xs text-gray-500 mb-2">{allRows.length} points</div>
      <MonthlyPivotTable
        rows={normalized}
        idField="sku_id"
        dateField="month_idx"
        valueField="value"
        idHeader="Article"
        baseMonth={(data as any)?.as_of_date?.slice(0, 7)}
        onRowClick={(id) => setModalArticle(String(id))}
        renderIdCell={(id) => <ArticleIdCell id={id} onClick={() => setModalArticle(id)} />}
        valueFormatter={(v) => formatIntegerWithColor(v)}
      />
      <SelectSupplierLocationModal open={!!modalArticle} articleId={modalArticle} onClose={() => setModalArticle(null)} />
    </div>
  );
};

export default ProjectedStockTab;


